import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'

import { TChatTypeUnion } from 'constants/types'
import { CHAT_TYPE } from 'constants/common'
import { IChat, IChatsState } from 'redux/store/chats/types'
import {
  getChats,
  getSearch,
  getTotal,
  getType
} from 'redux/store/chats/getters'
import {
  getChatListActionAsync,
  getLatestUnreadChat
} from 'redux/store/chats/actions'
import { CHAT_LIST_TABS } from 'features/ChatList/constants'
import { ACTIONS, SUBJECTS } from 'features/ChatList/Abilities'
import { useChatListAbility } from 'features/ChatList/Providers/ChatListAbilityProvider'
import useTabs, { TabsReturnActions, TabsReturnState } from 'hooks/useTabs'
import useInfiniteData from 'hooks/useInfiniteData'
import { setLoading } from '../../../redux/store/common/slice'
import { createChatRequest, GetChatListRequestArgs } from '../api'
import { routes } from '../../../router'
import useRouter from '../../../hooks/useRouter'
import { notification } from '../../../components/Notification'
import { BASE_SOCKET_TYPE, NOTIFICATIONS } from '../../../constants'
import { useAppDispatch } from '../../../redux/createStore'
import { DEFAULT_CHAT_LIST_LIMIT } from './constants'
import { getLastSocketMessage } from 'redux/store/chatSocket/getters'

type ContextProps = {
  state: {
    hasNextPage: boolean
  } & TabsReturnState &
    Pick<IChatsState, 'chats' | 'type' | 'search'>
  actions: {
    handleLoadMore: VoidFunction
    setSearch: (value: string) => void
  } & TabsReturnActions
}

const ChatListContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const ChatListContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch()
  const ability = useChatListAbility()
  const preselectedReceiver = localStorage.getItem('receiver')

  const _type = useSelector(getType)
  const _search = useSelector(getSearch)
  const _chats = useSelector(getChats)
  const _total = useSelector(getTotal)
  const _lastSocketMessage = useSelector(getLastSocketMessage)

  const [chats, setChats] = useState<IChat[]>(_chats)
  const [search, setSearch] = useState(_search)

  const timeoutRef = useRef<NodeJS.Timeout>()
  const router = useRouter()

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: CHAT_LIST_TABS,
    activeTab: _type || CHAT_LIST_TABS[0].key
  })

  const _getChatsList = useCallback(
    async (args: Partial<GetChatListRequestArgs>) => {
      const typedActiveTab = tabsState.activeTab as TChatTypeUnion
      const requestArgs = { ...args }
      // if we're not in the all chats list - passing type of chats to params
      if (typedActiveTab !== CHAT_LIST_TABS[0].key) {
        requestArgs.type = typedActiveTab
      }
      dispatch(getChatListActionAsync(requestArgs))
    },
    [dispatch, tabsState.activeTab]
  )

  const onOpenPreselectedChat = useCallback(
    async (receiver: string) => {
      try {
        dispatch(setLoading(true))
        const { data } = await createChatRequest({
          type: 'private',
          members: [receiver]
        })
        if (data.is_new) {
          notification.success({
            message:
              data.type === 'private'
                ? NOTIFICATIONS.PRIVATE_CHAT_CREATED
                : NOTIFICATIONS.GROUP_CHAT_CREATED
          })
        }
        router.navigate(`${routes.chat}/${data.uuid}`, { replace: true })
        localStorage.removeItem('receiver')
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, router]
  )

  useEffect(() => {
    return () => setSearch('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { limit, hasNextPage, handleLoadMore, reset, setOffset } =
    useInfiniteData({
      total: _total,
      limit: DEFAULT_CHAT_LIST_LIMIT,
      loadMore: _getChatsList
    })

  const _listenActiveTabChange = useCallback(() => {
    _getChatsList({ limit })
    reset()
    setChats([])
  }, [_getChatsList, limit, reset])

  const _listenSearchChange = useCallback(() => {
    if (search !== null) {
      const requestArgs = { limit, search }
      if (search.length) {
        requestArgs.search = search
      } else {
        // need to reset tab to default on search dismiss
        tabsActions.setActiveTab(CHAT_LIST_TABS[0].key)
      }
      _getChatsList(requestArgs)
    }
  }, [_getChatsList, limit, search, tabsActions])

  const _appendChatToBeginning = useCallback(() => {
    setOffset((prev) => prev + 1)
    dispatch(getLatestUnreadChat())
  }, [dispatch, setOffset])

  useEffect(() => {
    setChats(_chats)
  }, [_chats])

  useEffect(() => {
    // if new meassage chat not in the list - append it to the beginning
    if (
      (_lastSocketMessage?.type === BASE_SOCKET_TYPE.NEW_MESSAGE_ADDED ||
        _lastSocketMessage?.type === BASE_SOCKET_TYPE.LOCAL_NON_SOCKET_SEND) &&
      !chats.find((chat) => chat.uuid === _lastSocketMessage.payload.chat_id)
    ) {
      _appendChatToBeginning()
    }
    // eslint-disable-next-line
  }, [_lastSocketMessage])

  useEffect(() => {
    if (preselectedReceiver) {
      onOpenPreselectedChat(preselectedReceiver)
    } else {
      _listenActiveTabChange()
    }
    // eslint-disable-next-line
  }, [_listenActiveTabChange, preselectedReceiver])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(_listenSearchChange, 500)

    reset()
    setChats([])
  }, [_listenSearchChange, reset])

  useLayoutEffect(() => {
    tabsActions.setTabs(
      CHAT_LIST_TABS.map((i) => {
        let danger = false
        switch (i.key) {
          case CHAT_TYPE.CHANNEL:
            danger = ability.cannot(ACTIONS.VIEW, SUBJECTS.CHANNEL_CHATS)
            break
          case CHAT_TYPE.GROUP:
            danger = ability.cannot(ACTIONS.VIEW, SUBJECTS.GROUP_CHATS)
            break
          case CHAT_TYPE.PRIVATE:
            danger = ability.cannot(ACTIONS.VIEW, SUBJECTS.PRIVATE_CHATS)
            break
        }
        return {
          ...i,
          danger
        }
      })
    )
  }, [ability, tabsActions])

  const context = useMemo(
    () => ({
      state: {
        ...tabsState,
        type: _type,
        search,
        chats,
        hasNextPage
      },
      actions: {
        ...tabsActions,
        handleLoadMore,
        setSearch
      }
    }),
    [tabsState, _type, search, chats, hasNextPage, tabsActions, handleLoadMore]
  )

  return (
    <ChatListContext.Provider value={context}>
      {children}
    </ChatListContext.Provider>
  )
}

export const useChatListContext = () => useContext(ChatListContext)

export default ChatListContextProvider
